import {IFreePlayVoucherModel} from "./free-play-voucher.model.interface";
import {
    IFreePlayVoucherData,
    IFreePlayVoucherDelta
} from "../../../../common/interfaces/free-play/free-play-voucher-data.interface";
import {computed, observable, runInAction} from "mobx";
import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {Check, TimeSpan} from "@sparkware/app-utils";
import {FreePlayGameModel} from "../free-play-game/free-play-game.model";
import {NullableString} from "../../../../common/types/nullable-types";
import {FP_VOUCHER_TYPES} from "../../../../common/const/free_play_consts";

export abstract class VoucherBaseModel implements IFreePlayVoucherModel {
    constructor(voucherData: IFreePlayVoucherData,
                protected readonly services: IServiceFactoryExtended) {
        this._voucherData = voucherData;

    }

    abstract get voucherName(): string;
    abstract get voucherInGameTitle(): string;
    abstract get voucherDescription(): string;
    abstract get backgroundImageUrl(): string;
    abstract get iconUrl(): string;
    protected abstract _formatVoucherAmount(amount: number): string;


    @observable.ref
    protected _voucherData: IFreePlayVoucherData;

    dispose() {
    }

    get id(): string {
        return this._voucherData.ID;
    }

    get freePlayType(): FP_VOUCHER_TYPES {
        return this._voucherData.FreePlayTypeId;
    }

    @computed
    get freePlayGames(): FreePlayGameModel[] {
        return this._voucherData.GameTypeIDList
            .map(gameType => this.services.games.gamesMap[gameType])
            .filter(gameInfo => Boolean(gameInfo))
            .filter(gameInfo => this._voucherData.CurrentBalance > 0
                                && (Check.isNullOrUndefined(gameInfo.minBet) || gameInfo.minBet <= this._voucherData.CurrentBalance))
            .map(gameInfo => new FreePlayGameModel(this, gameInfo));
    }

    hasGame(gameType: number): boolean {
        return Boolean(this.freePlayGames.find(g => g.gameInfo.gameType === gameType));
    }


    private get isExpired(): boolean {
        return this._voucherData.ExpirationDate.getTime() < this.services.time.now();
    }

    get isValid(): boolean {
        return !this.isExpired && this.freePlayGames.length > 0;
    }

    updateVoucherData(newVoucherData: IFreePlayVoucherDelta): void {
        if(newVoucherData.ID !== this.id) {
            throw new Error('New voucher ID and existing voucher ID does not match');
        }
        runInAction(() => {
            this._voucherData = {
                ...this._voucherData,
                ...newVoucherData
            }
        });
    }

    get formattedOriginalAmount(): NullableString {
        if(Check.isNullOrUndefined(this._voucherData.OriginAmount)) {
            return null;
        }

        return this._formatVoucherAmount(this._voucherData.OriginAmount);
    }

    get formattedCurrentBalance(): string {
        return this._formatVoucherAmount(this._voucherData.CurrentBalance);
    }

    get timeToExpiration(): TimeSpan {

        /*
         "FREE_PLAY_EXPIRES_IN_TIME": "Expires in {expiration}",
        "FREE_PLAY_EXPIRES_IN_EXPIRED": "Expired",
        "FREE_PLAY_EXPIRES_IN_DAY": "Expires in {expiration} days",
         */

        return TimeSpan.fromMilliseconds(this._voucherData.ExpirationDate.getTime() - this.services.time.now());
    }

    get isCloseToExpiration(): boolean {
        return this.timeToExpiration.totalDays <= 1;
    }

    get expirationMessage(): string {
        if(this.isExpired) {
            return this.services.translation.translate('FREE_PLAY_EXPIRES_IN_EXPIRED');
        }

        const timeToExpirationRoundedToSeconds = TimeSpan.fromSeconds(Math.floor(this.timeToExpiration.totalSeconds));

        if(this.isCloseToExpiration) {
            return this.services.translation.translate('FREE_PLAY_EXPIRES_IN_TIME', {expiration:timeToExpirationRoundedToSeconds.toString({useTotalHours: true})});
        }

        return this.services.translation.translate('FREE_PLAY_EXPIRES_IN_DAY', {expiration: Math.floor(timeToExpirationRoundedToSeconds.totalDays).toString()});
    }
}